import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import CartComponent from "./cartComponent";
import { gaEvent } from "../../analytics";

/**
 * One cart drawer, one owner (EB-556).
 *
 * The drawer used to be mounted three times — in `HeaderBar`, in `ItemMaintenancePage` and in its
 * `Footer` — each with its own `open` flag. That was survivable while the only thing that opened it
 * was the button sitting next to it. It stopped being survivable when "Agregar al carrito" on the
 * product page had to open it: that button renders *inside* `PageLayout`, below the `HeaderBar`
 * that owns the drawer, so there was no state for it to reach.
 *
 * The provider renders the drawer itself rather than handing out a boolean, so there is no way to
 * take the state without taking the single instance that goes with it.
 */
type CartDrawerContextValue = {
  isOpen: boolean;
  /** `source` is the GA4 `view_cart` dimension: which button brought the shopper here. */
  openCart: (source: string) => void;
  closeCart: () => void;
};

/**
 * A no-op default rather than a throw. Plenty of components are rendered in tests without the app
 * around them, and a cart drawer that does not open is a far better failure there than a suite that
 * cannot mount a page at all.
 */
const noop = () => undefined;

const CartDrawerContext = createContext<CartDrawerContextValue>({
  isOpen: false,
  openCart: noop,
  closeCart: noop,
});

export const useCartDrawer = () => useContext(CartDrawerContext);

export const CartDrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const openCart = useCallback((source: string) => {
    gaEvent("view_cart", { source });
    setIsOpen(true);
  }, []);

  const closeCart = useCallback(() => setIsOpen(false), []);

  const value = useMemo(() => ({ isOpen, openCart, closeCart }), [isOpen, openCart, closeCart]);

  return (
    <CartDrawerContext.Provider value={value}>
      {children}
      <CartComponent open={isOpen} onClose={closeCart} />
    </CartDrawerContext.Provider>
  );
};
